import React from 'react';
import _ from 'lodash';
import { Plugin } from '@devexpress/dx-react-core';
import { SelectionState, IntegratedSelection } from '@devexpress/dx-react-grid';
import { TableSelection } from '@devexpress/dx-react-grid-material-ui';
import { useTheme } from '@mui/material';
import { TableStateContext } from '../TableStateContext';
import { ArrayUtils } from '../../../../utils/array.utils';

/**
 * The selection column, without the table's usual cell padding.
 *
 * <p>The column is 58px and the checkbox, with the padding MUI gives it, is about 42px. The
 * theme's 1.25rem a side leaves 28px of content box for it, so it does not centre -- it overflows
 * and sits off to one side, which is what made the checkbox look wrongly padded however
 * symmetric the cell's own padding was.
 *
 * <p>Zero here rather than a smaller number: the checkbox carries its own padding already, and
 * the column then centres it with the same space on both sides.
 */
const selectionCellStyle = { padding: 0, textAlign: 'center' as const };

export const SelectionCell = (props: any) => (
  <TableSelection.Cell {...props} style={selectionCellStyle} />
);

/**
 * The select-all cell is the one header cell DevExpress does not route through
 * `DefaultTableHeaderCell`, so it never picked up that component's
 * `background.tableHeader` and rendered white against the tinted header row.
 * Under `sticky` the header also scrolls over body rows, so it needs an opaque
 * background of its own rather than an inherited one.
 */
export const SelectionHeaderCell = (props: any) => {
  const { palette } = useTheme();
  return (
    <TableSelection.HeaderCell
      {...props}
      style={{
        ...selectionCellStyle,
        backgroundColor: palette.background.tableHeader,
      }}
    />
  );
};

export const SelectionPlugin = ({ ...props }) => {
  // @ts-expect-error TS(2339): Property 'state' does not exist on type 'unknown'.
  const { state, handlers, ...rest } = React.useContext(TableStateContext);
  return <SelectionPluginBase {...props} {...state} {...handlers} {...rest} />;
};

const mapIdsToEntities = (
  selection: Array<string | number> = [],
  entities: Record<string | number, unknown> = {}
) => {
  return selection.map((id) => entities[id]);
};

const SelectionPluginBase = ({
  showSelectAll = true,
  tableData = [],
  ...props
}) => {
  const [aggregateTableData, setTableData] = React.useState<
    Record<string | number, unknown>
  >({});

  React.useEffect(() => {
    if (!_.isEmpty(tableData)) {
      setTableData((prev) => ({
        ...prev,
        ...ArrayUtils.reduceArrayToObject(tableData),
      }));
    }
  }, [tableData]);

  const selection = ArrayUtils.mapEntitiesToIds(props.selection);
  return (
    <Plugin>
        <SelectionState
          selection={selection}
          onSelectionChange={(nextSelection) => {
            const enrichedPayload = mapIdsToEntities(
              nextSelection,
              aggregateTableData
            );
            props.onSelectionChange(enrichedPayload);
          }}
        />
        <IntegratedSelection />
        <TableSelection
          showSelectAll={showSelectAll}
          cellComponent={SelectionCell}
          headerCellComponent={SelectionHeaderCell}
        />
      </Plugin>
  );
};

export default SelectionPluginBase;
